'use client'

import { Star, ShoppingCart, Heart, Truck, Package } from 'lucide-react'
import { useState } from 'react'
import type { Product } from '@/types/product'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useCartStore } from '@/stores/cart'
import { useWishlistStore } from '@/stores/wishlist'
import { useCurrencyStore } from '@/stores/currency'

interface ProductDetailsModalProps {
  product: Product | null
  open: boolean
  onClose: () => void
}

export default function ProductDetailsModal({ product, open, onClose }: ProductDetailsModalProps) {
  const [selectedImage, setSelectedImage] = useState(0)
  const [quantity, setQuantity] = useState(1)
  const [addedToCart, setAddedToCart] = useState(false)

  const addItem = useCartStore(state => state.addItem)
  const { isInWishlist, toggleItem } = useWishlistStore()
  const getSymbol = useCurrencyStore(state => state.getSymbol)
  const convert = useCurrencyStore(state => state.convert)

  if (!product) return null

  const inWishlist = isInWishlist(product.id)

  const handleAddToCart = () => {
    addItem({
      product_id: product.id,
      title: product.title,
      image: product.images[0]?.url || '',
      price: product.pricing.current_price,
      original_price: product.pricing.original_price,
      vendor_name: product.vendor.name,
    }, quantity)
    
    setAddedToCart(true)
    setTimeout(() => setAddedToCart(false), 2000)
  }

  const handleToggleWishlist = () => {
    toggleItem(product.id)
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader onClose={onClose}>
          <DialogTitle>{product.title}</DialogTitle>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Left: Images */}
          <div className="space-y-4">
            {/* Main Image */}
            <div className="relative aspect-square overflow-hidden rounded-lg bg-muted">
              <img
                src={product.images[selectedImage]?.url || product.images[0]?.url}
                alt={product.title}
                className="w-full h-full object-cover"
              />
              {product.pricing.discount_percentage > 0 && (
                <div className="absolute top-3 left-3">
                  <div className="bg-accent text-accent-foreground px-3 py-1 rounded text-sm font-bold">
                    -{product.pricing.discount_percentage}% OFF
                  </div>
                </div>
              )}
            </div>

            {/* Thumbnail Images */}
            {product.images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto">
                {product.images.map((image, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`flex-shrink-0 w-20 h-20 rounded-md overflow-hidden border-2 transition-colors ${
                      selectedImage === idx ? 'border-primary' : 'border-border'
                    }`}
                  >
                    <img src={image.url} alt={image.alt} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Right: Details */}
          <div className="space-y-4">
            {/* Vendor */}
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs">
                {product.vendor.name}
              </Badge>
              {product.vendor.is_verified && (
                <Badge variant="default" className="text-xs bg-primary">
                  Verified
                </Badge>
              )}
              {product.badges.includes('choice') && (
                <Badge variant="default" className="text-xs bg-brand-orange">
                  BotTola Choice
                </Badge>
              )}
            </div>

            {/* Rating */}
            <div className="flex items-center gap-2">
              <div className="flex">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    className={`h-4 w-4 ${
                      i < Math.floor(product.rating.average)
                        ? 'fill-current text-yellow-500'
                        : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>
              <span className="text-sm text-muted-foreground">
                {product.rating.average} ({product.rating.count} reviews)
              </span>
            </div>

            {/* Price */}
            <div className="space-y-1">
              <div className="flex items-baseline gap-3">
                <span className="text-3xl font-bold text-brand-orange">
                  {getSymbol()}{convert(product.pricing.current_price).toLocaleString()}
                </span>
                {product.pricing.discount_percentage > 0 && (
                  <span className="text-lg text-muted-foreground line-through">
                    {getSymbol()}{convert(product.pricing.original_price).toLocaleString()}
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground">Tax included</p>
            </div>

            {/* Stock Status */}
            <div className="flex items-center gap-2">
              <Package className="h-4 w-4 text-muted-foreground" />
              <span className={`text-sm font-medium ${
                product.stock_status === 'in_stock' ? 'text-success' : 
                product.stock_status === 'low_stock' ? 'text-orange-500' : 'text-destructive'
              }`}>
                {product.stock_status === 'in_stock' ? 'In Stock' : 
                 product.stock_status === 'low_stock' ? `Only ${product.stock_quantity} left` : 'Out of Stock'}
              </span>
            </div>

            {/* Description */}
            <div>
              <h3 className="font-semibold mb-2">Description</h3>
              <p className="text-sm text-muted-foreground">{product.description}</p>
            </div>

            {/* Key Features */}
            {product.key_features && product.key_features.length > 0 && (
              <div>
                <h3 className="font-semibold mb-2">Key Features</h3>
                <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                  {product.key_features.map((feature, idx) => (
                    <li key={idx}>{feature}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Shipping Info */}
            <div className="flex items-center gap-4 p-3 bg-muted rounded-lg">
              <Truck className="h-5 w-5 text-muted-foreground" />
              <div className="flex-1">
                <p className="text-sm font-medium">
                  {product.shipping.free_shipping ? 'Free Shipping' : `Shipping: ${getSymbol()}${convert(product.shipping.cost)}`}
                </p>
                <p className="text-xs text-muted-foreground">
                  Estimated delivery: {product.shipping.estimated_days} days
                </p>
              </div>
            </div>

            {/* Quantity Selector */}
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium">Quantity:</span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1}
                >
                  -
                </Button>
                <span className="w-12 text-center font-medium">{quantity}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setQuantity(Math.min(product.stock_quantity, quantity + 1))}
                  disabled={quantity >= product.stock_quantity}
                >
                  +
                </Button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                variant="primary"
                className="flex-1"
                onClick={handleAddToCart}
                disabled={product.stock_status === 'out_of_stock'}
              >
                {addedToCart ? (
                  <>✓ Added to Cart</>
                ) : (
                  <>
                    <ShoppingCart className="h-5 w-5 mr-2" />
                    Add to Cart
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleToggleWishlist}
              >
                <Heart
                  className={`h-5 w-5 ${inWishlist ? 'fill-current text-destructive' : ''}`}
                />
              </Button>
            </div>

            {/* Category */}
            <div className="pt-4 border-t border-border">
              <p className="text-sm text-muted-foreground">
                Category: <span className="text-foreground font-medium">{product.category_name}</span>
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
